import { useRef, useState, type Ref, type RefObject } from "react";
import type { IncidentDescriptionOption, IncidentTicketConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { Spotlight } from "../ui/Spotlight";
import { tx, txr } from "../lib/tx";

/** Inert decoy bookmarks so the bar reads as a real browser's, not a single
 *  suspicious button — same convention as the AiAssistant browser home. */
const DECOY_BOOKMARKS: { label: string; icon: string }[] = [
  { label: "Webmail", icon: "\u2709" },
  { label: "Timesheet", icon: "\u23F1" },
];

const GENERIC_CATEGORIES = [
  "Hardware request",
  "Password reset",
  "Network connectivity",
  "Security Incident",
];
const GENERIC_PRIORITIES = ["Low", "Medium", "High", "Critical"];

type Phase = "home" | "intranet" | "portal" | "form" | "sent";

/** A single click-to-open dropdown field (Category / Priority). Not a
 *  native <select> — this project's convention is fully custom device UI
 *  so the open/closed state, blink cue, and "picked" highlight can be
 *  driven the same way as every other in-course interaction. */
function TicketDropdown({
  label,
  placeholder,
  options,
  value,
  onPick,
  autoFocus,
  btnRef,
}: {
  label: string;
  placeholder: string;
  options: string[];
  value: string | null;
  onPick: (v: string) => void;
  autoFocus?: boolean;
  btnRef?: Ref<HTMLButtonElement>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="itk-dd">
      <span className="itk-field-label">{label}</span>
      <button
        ref={btnRef}
        type="button"
        className={`itk-dd-btn ${!value && autoFocus ? "cf-blink-strong" : ""}`}
        onClick={() => {
          audio.click();
          setOpen((v) => !v);
        }}
      >
        <span className={value ? "itk-dd-val" : "itk-dd-placeholder"}>{value ?? placeholder}</span>
        <span className={`itk-dd-caret ${open ? "itk-dd-caret--open" : ""}`} aria-hidden>
          {"\u25BE"}
        </span>
      </button>
      {open && (
        <div className="itk-dd-menu" role="listbox">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`itk-dd-opt ${value === opt ? "itk-dd-opt--picked" : ""}`}
              onClick={() => {
                audio.click();
                onPick(opt);
                setOpen(false);
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Episode 11 — the incident-response ticketing game. A full-bleed floating
 * "browser window" overlay (like IncidentReport/UsbReveal/MfaFatigue — this
 * course's convention for cinematic decision devices, not a desk-monitor
 * SCREEN_KINDS app, so it never depends on which 3D camera pose is
 * currently active). See the JSDoc on {@link IncidentTicketConfig} for the
 * full navigate-then-fill flow this now implements.
 */
export function IncidentTicket({
  config,
  onEnded,
}: {
  config: IncidentTicketConfig;
  onEnded: (key: "perfect" | "partial" | "risky" | "misfiled", optionId: string, eventId?: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>("home");
  const [category, setCategory] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [descOpen, setDescOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<IncidentDescriptionOption | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const descPlaceholderRef = useRef<HTMLButtonElement | null>(null);
  const newTicketRef = useRef<HTMLButtonElement | null>(null);
  const intranetLinkRef = useRef<HTMLButtonElement | null>(null);
  const categoryBtnRef = useRef<HTMLButtonElement | null>(null);
  const priorityBtnRef = useRef<HTMLButtonElement | null>(null);

  const categoryOptions = config.categoryOptions?.length ? config.categoryOptions : GENERIC_CATEGORIES;
  const priorityOptions = config.priorityOptions?.length ? config.priorityOptions : GENERIC_PRIORITIES;

  const openIntranet = () => {
    audio.click();
    setPhase("intranet");
  };
  const openPortal = () => {
    audio.click();
    setPhase("portal");
  };
  const openForm = () => {
    audio.click();
    setPhase("form");
  };

  const openDescription = () => {
    audio.click();
    setDescOpen(true);
  };

  const pick = (opt: IncidentDescriptionOption) => {
    audio.click();
    setSelectedId(opt.id);
  };

  const submit = () => {
    const opt = config.options.find((o) => o.id === selectedId);
    if (!opt) return;
    audio.ui("success");
    setChosen(opt);
    setPhase("sent");
  };

  const finish = () => {
    if (!chosen) return;
    const categoryOk = !config.category || category === config.category;
    const priorityOk = !config.priority || priority === config.priority;
    const key = chosen.key === "perfect" && (!categoryOk || !priorityOk) ? "misfiled" : chosen.key;
    onEnded(key, chosen.id, chosen.eventId);
  };

  const chrome = (url: string) => (
    <div className="cgpt-chrome">
      <span className="cgpt-dot cgpt-dot--r" aria-hidden />
      <span className="cgpt-dot cgpt-dot--y" aria-hidden />
      <span className="cgpt-dot cgpt-dot--g" aria-hidden />
      <div className="cgpt-omni" aria-hidden>
        <span className="cgpt-omni-url">{url}</span>
      </div>
    </div>
  );

  if (phase === "home") {
    return (
      <div className="itk">
        <div className="itk-window">
          {chrome("New Tab")}
          <div className="cgpt-bookbar" role="toolbar" aria-label={tx("Bookmarks")}>
            <button type="button" className="cgpt-bookmark hot cf-blink-strong" onClick={openIntranet}>
              <span className="cgpt-bookmark-ico" aria-hidden>
                {"\u2302"}
              </span>
              {config.bookmarkLabel ?? tx("Intranet")}
            </button>
            {DECOY_BOOKMARKS.map((b) => (
              <span key={b.label} className="cgpt-bookmark" aria-hidden>
                <span className="cgpt-bookmark-ico">{b.icon}</span>
                {b.label}
              </span>
            ))}
          </div>
          <div className="cgpt-newtab">
            <span className="cgpt-newtab-logo" aria-hidden>
              {"\u2302"}
            </span>
            {/* The whole hint (not just the bookmark button above) is
                clickable, mirroring AiAssistant's new-tab hint fix — the
                arrow used to be purely decorative, so a learner who tried to
                click IT (a natural instinct) got nothing. */}
            <button type="button" className="cgpt-newtab-hint cf-pulse-target" onClick={openIntranet}>
              <span className="cgpt-newtab-arrow" aria-hidden>
                {"\u2191"}
              </span>
              <p>{txr("You're still at your desk. Open the {v} bookmark above to report what just happened.", { v: config.bookmarkLabel ?? tx("Intranet") })}</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "intranet") {
    return (
      <div className="itk">
        <div className="itk-window" ref={rootRef}>
          <Spotlight containerRef={rootRef} targets={[intranetLinkRef]} />
          {chrome(config.intranetUrl ?? "intranet.northbay.internal")}
          <div className="itk-page itk-intranet">
            <div className="itk-intranet-hero">
              <span className="itk-intranet-logo" aria-hidden>
                {tx("N")}
              </span>
              <div className="itk-intranet-title">{tx("NorthBay Financial — Staff Intranet")}</div>
            </div>
            <div className="itk-intranet-grid">
              <span className="itk-intranet-tile itk-intranet-tile--muted">{tx("Company news")}</span>
              <span className="itk-intranet-tile itk-intranet-tile--muted">{tx("Timesheet")}</span>
              <span className="itk-intranet-tile itk-intranet-tile--muted">{tx("HR benefits")}</span>
              <button
                type="button"
                ref={intranetLinkRef}
                className="itk-intranet-tile itk-intranet-tile--hot cf-pulse-target"
                onClick={openPortal}
              >
                <span className="itk-intranet-tile-ico" aria-hidden>
                  {"\u2691"}
                </span>
                {txr("IT Service Desk<0>Report an incident or request help</0>", {}, [<span className="itk-intranet-tile-sub" />])}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "portal") {
    return (
      <div className="itk">
        <div className="itk-window" ref={rootRef}>
          <Spotlight containerRef={rootRef} targets={[newTicketRef]} />
          {chrome(config.portalUrl ?? "itsm.northbay.internal")}
          <div className="itk-page itk-portal">
            <div className="itk-head">
              <div className="itk-head-title">{config.portalName ?? tx("NorthBay IT Service Desk")}</div>
            </div>
            <p className="itk-instructions">{txr("Welcome back, {v}. What do you need help with?", { v: config.requester ?? tx("A. Morgan") })}</p>
            <button type="button" ref={newTicketRef} className="itk-newticket-btn cf-pulse-target" onClick={openForm}>
              <span aria-hidden>{"+"}</span> {tx("New incident ticket")}
            </button>
            <div className="itk-portal-decoys">
              <span className="itk-portal-decoy">{tx("My open tickets (0)")}</span>
              <span className="itk-portal-decoy">{tx("Knowledge base")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "sent" && chosen) {
    return (
      <div className="itk">
        <div className="itk-window">
          {chrome(config.portalUrl ?? "itsm.northbay.internal/incidents/new")}
          <div className="itk-sent">
            <span className="itk-sent-ico" aria-hidden>
              {"\u2713"}
            </span>
            <div className="itk-sent-title">{txr("Ticket {v} submitted", { v: config.ticketNumber ?? tx("INC0048213") })}</div>
            <p className="itk-sent-body">{tx("IT/Security has received your report. Here's the description you sent:")}</p>
            <div className="itk-sent-desc">{chosen.text}</div>
            <button type="button" className="btn itk-sent-continue" onClick={finish}>
              {tx("Continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // phase === "form"
  const formReady = !!category && !!priority;
  const canSubmit = !!selectedId;
  // Every step of the ticket form now gets its own single highlighted
  // target — category, then priority, then the description block, then the
  // right description option, then submit — so a learner never has to guess
  // what's next (2026-07-26: "highlight what to click/select in each step").
  const spotlightTargets: RefObject<HTMLElement | null>[] = !category
    ? [categoryBtnRef]
    : !priority
      ? [priorityBtnRef]
      : !descOpen
        ? [descPlaceholderRef]
        : canSubmit
          ? [submitRef]
          : [optionsRef];

  return (
    <div className="itk">
      <div className="itk-window" ref={rootRef}>
        {spotlightTargets.length > 0 && <Spotlight containerRef={rootRef} targets={spotlightTargets} />}
        {chrome(config.portalUrl ?? "itsm.northbay.internal/incidents/new")}

        <div className="itk-page">
          <div className="itk-head">
            <div className="itk-head-title">{tx("New incident ticket")}</div>
            <div className="itk-head-ticket">{txr("Ticket {v} · Draft", { v: config.ticketNumber ?? tx("INC0048213") })}</div>
          </div>

          <div className="itk-fields">
            <div className="itk-field">
              <span className="itk-field-label">{tx("Requester")}</span>
              <span className="itk-field-value">{config.requester ?? tx("A. Morgan")}</span>
              <span className="itk-field-note">{tx("Auto-filled from your sign-in")}</span>
            </div>
            <TicketDropdown
              label={tx("Category")}
              placeholder={tx("Select a category…")}
              options={categoryOptions}
              value={category}
              onPick={setCategory}
              autoFocus
              btnRef={categoryBtnRef}
            />
            <TicketDropdown
              label={tx("Priority")}
              placeholder={tx("Select a priority…")}
              options={priorityOptions}
              value={priority}
              onPick={setPriority}
              autoFocus={!!category}
              btnRef={priorityBtnRef}
            />
          </div>

          {formReady && (
            <div className="itk-desc-section">
              <span className="itk-field-label">{config.descriptionLabel ?? tx("Incident description")}</span>
              {!descOpen ? (
                <button type="button" ref={descPlaceholderRef} className="itk-desc-placeholder cf-pulse-target" onClick={openDescription}>
                  {tx("Click to write the incident description…")}
                </button>
              ) : (
                <>
                  <p className="itk-instructions">
                    {config.instructions ??
                      tx("Pick the block below that belongs in the description field. Only one of these actually reads like a real incident report.")}
                  </p>
                  <div className="itk-options" ref={optionsRef}>
                    {config.options.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`itk-option ${selectedId === opt.id ? "itk-option--picked" : ""}`}
                        onClick={() => pick(opt)}
                      >
                        <span className="itk-option-radio" aria-hidden>
                          {selectedId === opt.id ? "\u25C9" : "\u25CB"}
                        </span>
                        <span className="itk-option-text">{opt.text}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            ref={submitRef}
            className={`btn itk-submit ${canSubmit ? "cf-pulse-target" : ""}`}
            disabled={!canSubmit}
            onClick={submit}
          >
            {config.submitLabel ?? tx("Submit ticket")}
          </button>
        </div>
      </div>
    </div>
  );
}
